import React, { useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  Input,
  List,
  ListItem,
  Icon,
  useToast,
  Flex,
  Badge,
  Divider
} from '@chakra-ui/react';
import { FiUpload, FiCheck, FiX } from 'react-icons/fi';

// Methodist brand colors
const methodistBlue = '#0066CC';
const methodistGreen = '#38A169';
const methodistRed = '#E53E3E';

interface Narration {
  id: string;
  title: string;
  audioUrl: string | null;
}

interface AudioUploadDebuggerProps {
  onUpload: (audioUrl: string, narrationId?: string) => Promise<void>;
  narrations: Narration[];
}

const AudioUploadDebugger: React.FC<AudioUploadDebuggerProps> = ({ onUpload, narrations }) => {
  const [selectedNarrationId, setSelectedNarrationId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const toast = useToast();

  const handleUpload = async () => {
    if (!audioUrl) {
      toast({
        title: 'Error',
        description: 'Please enter an audio URL',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      });
      return;
    }

    setIsUploading(true);
    try {
      await onUpload(audioUrl, selectedNarrationId || undefined);
      setAudioUrl('');
      toast({
        title: 'Success',
        description: 'Audio URL uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload audio URL',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        <Box>
          <Text fontWeight="bold" mb={2} color={methodistBlue}>Audio URL</Text>
          <Input
            value={audioUrl}
            onChange={(e) => setAudioUrl(e.target.value)}
            placeholder="Enter audio URL"
            size="sm"
          />
        </Box>

        <Box>
          <Text fontWeight="bold" mb={2} color={methodistBlue}>Select Narration</Text>
          <List spacing={2}>
            {narrations.map((narration) => (
              <ListItem 
                key={narration.id}
                onClick={() => setSelectedNarrationId(narration.id)}
                cursor="pointer"
                p={2}
                bg={selectedNarrationId === narration.id ? `${methodistBlue}10` : 'transparent'}
                borderRadius="md"
                _hover={{ bg: `${methodistBlue}05` }}
              >
                <Flex justify="space-between" align="center">
                  <Text>{narration.title}</Text>
                  {narration.audioUrl ? (
                    <Badge colorScheme="green">
                      <Flex align="center">
                        <Icon as={FiCheck} mr={1} />
                        Has Audio
                      </Flex>
                    </Badge>
                  ) : (
                    <Badge colorScheme="red">
                      <Flex align="center">
                        <Icon as={FiX} mr={1} />
                        No Audio
                      </Flex>
                    </Badge>
                  )}
                </Flex>
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider />

        <Button
          leftIcon={<Icon as={FiUpload} />}
          colorScheme="blue"
          onClick={handleUpload}
          isLoading={isUploading}
          loadingText="Uploading..."
          isDisabled={!audioUrl}
        >
          Upload Audio URL
        </Button>
      </VStack>
    </Box>
  );
};

export default AudioUploadDebugger;
